import * as XLSX from "xlsx";

function splitCsvLine(line: string, delimiter: string) {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (ch === delimiter && !quoted) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((v) => v.trim());
}

function looksLikePhone(value: string) {
  return /^[+\d][\d\s().-]{5,}$/.test(value.trim());
}

function normalizeHeader(h: string) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function aliasRow(row: Record<string, unknown>) {
  const out: Record<string, unknown> = { ...row };
  const entries = Object.entries(row);
  for (const [rawKey, value] of entries) {
    const key = normalizeHeader(rawKey);
    if (["name", "full_name", "contact_name", "customer", "customer_name"].includes(key) && !out.name) {
      out.name = value;
    }
    if (["phone", "mobile", "number", "phone_number", "mobile_number", "contact"].includes(key) && !out.phone) {
      out.phone = value;
    }
    if (["company", "organisation", "organization", "org", "firm"].includes(key) && !out.company) {
      out.company = value;
    }
    if (["email", "mail", "e_mail"].includes(key) && !out.email) {
      out.email = value;
    }
    if (["notes", "note", "remark", "remarks"].includes(key) && !out.notes) {
      out.notes = value;
    }
  }
  return out;
}

function parseCsv(text: string): Record<string, unknown>[] {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const delimiter = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
  const headers = splitCsvLine(lines[0], delimiter).map((h) => h.replace(/"/g, ""));
  if (headers.length === 1 && looksLikePhone(headers[0])) {
    return lines.map((line) => {
      const phone = splitCsvLine(line, delimiter)[0] || line;
      return aliasRow({ phone, name: phone });
    });
  }
  return lines.slice(1).map((line) => {
    const cols = splitCsvLine(line, delimiter);
    const row: Record<string, unknown> = {};
    headers.forEach((h, i) => {
      row[h || `col${i + 1}`] = cols[i] || "";
    });
    if (!row.phone && cols[0] && looksLikePhone(String(cols[0])) && !looksLikePhone(headers[0])) {
      row.phone = cols[0];
      if (!row.name) row.name = cols[1] || cols[0];
    }
    return aliasRow(row);
  });
}

export async function parseContactSheet(file: File): Promise<Record<string, unknown>[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv") || name.endsWith(".txt") || name.endsWith(".tsv")) {
    const rows = parseCsv(await file.text());
    if (!rows.length) throw new Error("The file has no data rows.");
    return rows;
  }
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const first = wb.SheetNames[0];
    if (!first) throw new Error("The workbook has no sheets.");
    const rows = XLSX.utils
      .sheet_to_json<Record<string, unknown>>(wb.Sheets[first], {
        defval: "",
        raw: false,
      })
      .map((row) => aliasRow(row));
    if (!rows.length) throw new Error("The file has no data rows.");
    return rows;
  }
  throw new Error("Upload an Excel (.xlsx) or CSV file.");
}
